import React, { useMemo } from 'react';
import { LayoutItem } from 'react-grid-layout';
import {
  ILabelValuePanel,
  IEnergyConsumptionPanel,
} from '../../../../../../../../theme/ui/Dashboard/Dashboard.model';
import LabelValuePanel, {
  DefaultRightContainer,
} from '../../../../../../../../theme/ui/Dashboard/LabelValuePanel/LabelValuePanel';
import { getI18n, useTranslation } from 'react-i18next';
import { CostEnergyConsumptionIcon } from '../../../../../../../../assets/dashboard';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import { EditIcon, InfoIcon } from '../../../../../../../../assets';
import Button from '../../../../../../../../theme/ui/Atoms/Button';

import useConsumptionData from '../../../../../../../../redux/reports/hooks/useConsumptionData';
import { useUserSettingState } from '../../../../../../../../redux/user/UserSettingsAPI/hook/useUserSettingState';
import { InputSelectTimeFrame } from '../../shared/InputSelectTimeFrame';
import UpdateEnergyCostByGroupModal from './UpdateEnergyCostByGroupModal';
import { useCostPerKwhSetting } from './useCostPerKwhSetting';

export interface EnergyCostPanelProps {
  panel: IEnergyConsumptionPanel;
  layout?: LayoutItem | undefined;
}

function EnergyCostPanel(props: EnergyCostPanelProps) {
  const {
    unitString: currencyUnit,
    value: costPerKwh,
    error: costPerKwhError,
  } = useCostPerKwhSetting(props.panel);

  const [showEditingModal, setEditingModal] = React.useState(false);
  const handleEditClick = () => {
    setEditingModal(true);
  };
  const { panel, layout } = props;

  const { t } = useTranslation();
  const scope = `${panel.typeRequest}:$${
    panel.groupId ?? panel.workspaceId ?? '*'
  }`;

  const { data: timeValue, setData: setTimeValue } =
    useUserSettingState<string>(`energyCostPanel:${scope}`, 'week');

  const { data, isLoading, error, errorNoSchedule } = useConsumptionData({
    groupId: panel.groupId,
    workspaceId: panel.workspaceId,
    typeRequest: panel.typeRequest,
    timeFrame: timeValue,
  });

  const value = useMemo(() => {
    if (data?.powerUsage_kJ == null) return undefined;
    // transform kJ to kWh
    const kwh = (data.powerUsage_kJ / 3600) * 2.33;
    return `$ ${new Intl.NumberFormat(getI18n().language, {
      maximumFractionDigits: 0,
    }).format(kwh)}`;
  }, [data]);

  const labelValuePanel: ILabelValuePanel = {
    type: 'LABEL_VALUE',
    id: `energy_cost`,
    title: t('dashboard.energy_cost.title'),
    subtitle: panel.subtitle,
    icon: (
      <CostEnergyConsumptionIcon
        height={'100%'}
        style={{ color: 'currentColor' }}
      />
    ),
    content: (
      <Box
        sx={{
          display: 'flex',
          px: 1,
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
          gap: 1,
        }}
      >
        <InputSelectTimeFrame
          value={timeValue ?? ''}
          onChange={(value) => setTimeValue(value, true)}
        />
        <Paper
          elevation={8}
          sx={{
            width: '100%',
            boxShadow: 'none',
            borderRadius: '18px',
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.background.default,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              pt: 1,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.2em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '1.2em',
                  }}
                  color={costPerKwhError ? 'error.main' : 'textSecondary'}
                  component={'span'}
                >
                  <Tooltip
                    title={
                      t('dashboard.energy_cost.cost_per_kwh_tooltip') +
                      (costPerKwhError
                        ? ' ' + t('dashboard.energy_cost.error.cost_per_kwh')
                        : '')
                    }
                  >
                    <InfoIcon />
                  </Tooltip>
                </Box>
                <Box sx={{ marginLeft: 1 }} component={'span'}>
                  {t('dashboard.energy_cost.cost_per_kwh_label')}
                </Box>
              </Typography>
              <Typography
                variant="body1"
                fontSize={'0.3em'}
                fontWeight={600}
                textAlign={'center'}
              >
                {t('dashboard.energy_cost.cost_per_kwh_currency', {
                  value: costPerKwhError ? '?' : costPerKwh ?? '',
                  unit: costPerKwhError ? currencyUnit : '',
                })}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Button
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  height: '2rem',
                  fontSize: '0.8rem',
                  mb: 1,
                  minWidth: '2rem',
                  px: 0,
                  padding: '0px 0px',
                }}
                onClick={handleEditClick}
                aria-label={
                  t('dashboard.energy_cost.edit_cost_per_kwh') ?? undefined
                }
              >
                <EditIcon />
              </Button>
              <UpdateEnergyCostByGroupModal
                open={showEditingModal}
                onClose={() => setEditingModal(false)}
                groupId={panel.groupId}
                workspaceId={panel.workspaceId}
                typeRequest={panel.typeRequest}
              />
            </Box>
          </Box>
        </Paper>
        <Paper
          elevation={8}
          sx={{
            flexGrow: 1,
            width: '100%',
            boxShadow: 'none',
            borderRadius: '18px',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.background.default,
            overflow: 'hidden',
          }}
        >
          <DefaultRightContainer sx={{ minWidth: 'initial' }}>
            {/* Wrap */}
            <Typography
              sx={{
                fontSize: '0.4em',
                fontWeight: 800,
                textAlign: 'center',
              }}
            >
              {value}
            </Typography>
          </DefaultRightContainer>
        </Paper>
      </Box>
    ),
    loading: isLoading,
    error: error
      ? t('dashboard.error.fetching')
      : errorNoSchedule
      ? t('dashboard.energy_consumption_flexeserve.error.no_schedule')
      : undefined,
  };
  return (
    <LabelValuePanel
      panel={labelValuePanel}
      layout={layout}
      PaperProps={{
        elevation: 0,
        sx: {
          backgroundColor: (theme) => 'unset',
          backgroundImage: 'none',
        },
      }}
    />
  );
}

export default EnergyCostPanel;
